import { addDays, monthName, startOfWeek, weekdayName } from '@app/shared'
import type { ISODate, LearningStreak } from '@app/shared'

import { durationPhrase } from './derive'
import { Flame } from './icons'

// ============================================================================
// THE COMBINED ACTIVITY HEATMAP — docs/design/handoff/"LifeOS Learning.dc.html", option 9a.
//
// The one dark card on this screen: ink, radius 16, padding 18/22. Twelve Monday-started columns
// of seven days, 5px gaps, radius 3, five teal steps on `learn-heat-0`, a legend, and a month
// ruler underneath.
//
// BOTH TRACKS, DELIBERATELY. The card says so in its own title, because two tracks summed into one
// grid without saying so would let a green week hide the fact that one of them has not been
// touched in three days. The per-track story is on the cards above it.
//
// THE LAST COLUMN IS SHORT, as the Journal heatmap's is: the rest of this week has not happened,
// and drawing four "nothing logged" squares for days that do not exist yet is the one thing this
// screen is built not to do. Today carries a ring so it is findable in the grid.
// ============================================================================

const WEEKS = 12

/** Five steps by minutes logged. The thresholds are the card's own, and the legend shows them. */
function level(minutes: number): number {
  if (minutes === 0) return 0
  if (minutes < 30) return 1
  if (minutes < 50) return 2
  if (minutes < 75) return 3
  return 4
}

const FILL = [
  'bg-learn-heat-0',
  'bg-learn-heat-1',
  'bg-learn-heat-2',
  'bg-learn-heat-3',
  'bg-learn-heat-4',
]

export function ActivityHeatmap({ streak, today }: { streak: LearningStreak; today: ISODate }) {
  const byDate = new Map(streak.days.map((d) => [d.date, d]))
  const from = startOfWeek(addDays(today, -(WEEKS - 1) * 7))

  // Columns of Monday-started weeks. The run stops at today, so the newest column is short.
  const weeks: ISODate[][] = []
  for (let d = from; d <= today; d = addDays(d, 1)) {
    if (weeks.length === 0 || d === startOfWeek(d)) weeks.push([])
    weeks[weeks.length - 1]!.push(d)
  }

  // One label per run of columns that begin in the same month — the ruler under the grid.
  const labels: Array<{ month: string; columns: number }> = []
  for (const week of weeks) {
    const label = monthName(week[0]!, true)
    const last = labels[labels.length - 1]
    if (last && last.month === label) last.columns += 1
    else labels.push({ month: label, columns: 1 })
  }

  return (
    <section className="flex flex-none flex-col gap-[14px] rounded-context bg-slab px-[22px] py-[18px]">
      <header className="flex items-center gap-[11px]">
        <h2 className="text-[12.5px] font-semibold leading-none text-on-dark">
          Both tracks · last {weeks.length} weeks
        </h2>
        {streak.current > 0 && (
          <span className="flex items-center gap-[6px] rounded-pill bg-ink-raised px-[10px] py-1">
            <Flame size={11} className="text-flame" />
            <span className="text-[10.5px] font-semibold leading-none text-flame">
              {streak.current}-day streak
            </span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-[7px]" aria-hidden>
          <span className="text-[10px] font-normal leading-none text-ink-3">less</span>
          {FILL.map((fill) => (
            <span key={fill} className={`size-[10px] rounded-[3px] ${fill}`} />
          ))}
          <span className="text-[10px] font-normal leading-none text-ink-3">more</span>
        </div>
      </header>

      <div className="flex gap-[5px]">
        <div
          className="flex flex-col justify-between py-px text-[9px] font-normal leading-none text-ink-3"
          aria-hidden
        >
          <span>M</span>
          <span>W</span>
          <span>F</span>
        </div>
        <div className="flex flex-1 gap-[5px]">
          {weeks.map((week) => (
            <div key={week[0]} className="flex flex-1 flex-col gap-[5px]">
              {week.map((date) => {
                const day = byDate.get(date)
                const minutes = day?.minutes ?? 0
                return (
                  <span
                    key={date}
                    title={
                      day
                        ? `${weekdayName(date, true)} ${date} · ${day.items} ${day.items === 1 ? 'item' : 'items'} · ${durationPhrase(minutes)}`
                        : `${weekdayName(date, true)} ${date} · nothing logged`
                    }
                    className={[
                      'h-[14px] rounded-[3px]',
                      FILL[level(minutes)],
                      date === today ? 'outline outline-[1.5px] outline-offset-1 outline-learn-heat-4' : '',
                    ].join(' ')}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <div
        className="flex items-center gap-[9px] text-[10.5px] font-normal leading-none text-ink-3"
        aria-hidden
      >
        {labels.map((label, i) => (
          <span key={`${label.month}-${i}`} className="flex flex-1 items-center gap-[9px]">
            {label.month}
            <span className="h-px flex-1 bg-ink-raised" />
          </span>
        ))}
        <span className="size-[5px] rounded-full bg-learn-heat-4" />
        <span className="text-learn-heat-4">today</span>
      </div>
    </section>
  )
}

export function ActivityHeatmapSkeleton() {
  return (
    <section className="flex flex-none flex-col gap-[14px] rounded-context bg-slab px-[22px] py-[18px]">
      <span className="sr-only">Loading…</span>
      <div className="h-[13px] w-[190px] rounded-full bg-ink-raised" aria-hidden />
      <div className="flex gap-[5px]" aria-hidden>
        {Array.from({ length: WEEKS }, (_, w) => (
          <div key={w} className="flex flex-1 flex-col gap-[5px]">
            {Array.from({ length: 7 }, (_, d) => (
              <span key={d} className="h-[14px] rounded-[3px] bg-learn-heat-0" />
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}
